import type { IVerbaScholaLesson, IVerbaScholaLessonGeneratedMaterial, IVerbaScholaLessonGrammarTarget, IVerbaScholaLessonPlanSection,
    IVerbaScholaLessonVocabularyTarget } from '../api/VerbaScholaApi';
import type { IVerbaScholaAiPreview, IVerbaScholaAiReviewChange } from '../api/VerbaScholaLessonAiApi';
import { planSectionPanel } from './lessonPlanCards';
import { activityTypeLabel, quizFieldLabels } from './VerbaScholaQuizRevisionPreview';

/** The lesson builder page that shows a review stop. */
export type ReviewPage = 'description' | 'plan' | 'exercises';
/** The saved lesson, every proposed change, or the saved lesson with only the changes the teacher includes. */
export type ReviewVersion = 'before' | 'proposal' | 'yours';

const EXERCISE_KINDS = new Set<IVerbaScholaAiReviewChange['kind']>(['exercise_addition', 'exercise_addition_step', 'exercise_removal', 'quiz_field', 'quiz_settings',
    'choice_addition', 'choice_deletion', 'choice_order', 'speaking_addition', 'speaking_deletion', 'speaking_order',
    'typed_addition', 'typed_deletion', 'typed_order', 'line_addition', 'line_deletion', 'line_order',
    'step_addition', 'step_deletion', 'step_order']);
const PLAN_KINDS = new Set<IVerbaScholaAiReviewChange['kind']>(['plan_section_field', 'generated_material_field',
    'content_replacement', 'plan_section_addition', 'generated_material_addition', 'plan_section_removal']);

/** The builder pages in the order a review visits them. */
export const REVIEW_PAGE_ORDER: ReviewPage[] = ['description', 'plan', 'exercises'];

/**
 * The builder page that shows each stop. Language targets appear on both Description and Lesson plan: a proposal
 * that also changes lesson details, or else plan cards, shows them on that page, so every page of one review agrees
 * on the order. A proposal of targets alone shows them on the page under review.
 */
export function stopPages(preview: IVerbaScholaAiPreview, current: ReviewPage): (stop: IVerbaScholaAiReviewChange) => ReviewPage {
    const stops = preview.review_changes ?? [];
    const targets: ReviewPage = stops.some(stop => stop.kind === 'lesson_field') ? 'description'
        : stops.some(stop => PLAN_KINDS.has(stop.kind)) ? 'plan' : current === 'exercises' ? 'description' : current;
    return stop => EXERCISE_KINDS.has(stop.kind) ? 'exercises' : PLAN_KINDS.has(stop.kind) ? 'plan' : stop.kind === 'lesson_field' ? 'description' : targets;
}

export function previewTouchesExercises(preview: IVerbaScholaAiPreview): boolean {
    return (preview.review_changes ?? []).some(stop => EXERCISE_KINDS.has(stop.kind)) || Boolean(preview.replaced_exercises?.length);
}

const lessonFieldLabels: Record<string, string> = { title: 'Lesson title', brief: 'Lesson brief', description: 'Description',
    duration_minutes: 'Class length', exercise_duration_minutes: 'Exercise target', proficiency_level: 'Proficiency',
    character_set: 'Characters', pedagogy_approach: 'Teaching approach' };
// The Description editor's order, top to bottom.
const DESCRIPTION_ORDER = ['title', 'description', 'brief', 'proficiency_level', 'duration_minutes', 'exercise_duration_minutes', 'character_set', 'pedagogy_approach'];
const rowFieldLabels: Record<string, string> = { title: 'Title', body: 'Text', duration_minutes: 'Minutes', readiness_status: 'Ready status',
    body_text: 'Text', material_format: 'Format', status: 'Ready status', hanzi: 'Word', pinyin: 'Pinyin', meaning: 'Meaning',
    pattern: 'Pattern', example: 'Example' };
export const PLAN_PANEL_ORDER = ['context', 'outcomes', 'language', 'materials', 'preparation', 'support', 'procedure', 'assessment'] as const;

export interface ReviewNames {
    sections?: { id: number; title: string; section_type?: string | null }[];
    materials?: { id: number; title: string }[];
    vocabulary?: { id: number; hanzi: string }[];
    grammar?: { id: number; pattern: string }[];
    exercises?: { id: number; title: string }[];
    steps?: { id: number; title: string; verbaschola_lesson_exercise_id?: number }[];
}

/** A short teacher-facing name for one review stop. */
export function stopLabel(stop: IVerbaScholaAiReviewChange, preview: IVerbaScholaAiPreview, names: ReviewNames = {}): string {
    const step = (id?: number) => names.steps?.find(row => row.id === id)?.title
        ?? preview.exercise_step_order?.find(row => row.step_id === id)?.before_title ?? 'Activity';
    const section = (id?: number) => names.sections?.find(row => row.id === id)?.title
        ?? preview.content_replacements?.find(row => row.target_type === 'plan_section' && row.target_id === id)?.title ?? 'Plan card';
    const material = (id?: number) => names.materials?.find(row => row.id === id)?.title
        ?? preview.content_replacements?.find(row => row.target_type === 'generated_material' && row.target_id === id)?.title ?? 'Material';
    switch (stop.kind) {
    case 'lesson_field': return lessonFieldLabels[stop.field ?? ''] ?? 'Lesson detail';
    case 'language_target_field': {
        const target = stop.target_type === 'grammar_target' ? names.grammar?.find(row => row.id === stop.target_id)?.pattern
            : names.vocabulary?.find(row => row.id === stop.target_id)?.hanzi;
        return `${stop.target_type === 'grammar_target' ? 'Grammar' : 'Vocabulary'}${target ? `: ${target}` : ''} · ${rowFieldLabels[stop.field ?? ''] ?? stop.field}`;
    }
    case 'target_addition': {
        const vocabulary = (preview.vocabulary_additions ?? []).find(row => row.client_key === stop.client_key);
        const grammar = (preview.grammar_additions ?? []).find(row => row.client_key === stop.client_key);
        return vocabulary ? `New vocabulary: ${vocabulary.hanzi}` : `New grammar: ${grammar?.pattern ?? 'target'}`;
    }
    case 'plan_section_field': return `${section(stop.target_id)} · ${rowFieldLabels[stop.field ?? ''] ?? stop.field}`;
    case 'generated_material_field': return `${material(stop.target_id)} · ${rowFieldLabels[stop.field ?? ''] ?? stop.field}`;
    case 'content_replacement': return `${stop.target_type === 'generated_material' ? material(stop.target_id) : section(stop.target_id)} · Rewritten text`;
    case 'plan_section_addition': {
        const title = (preview.plan_section_additions ?? []).find(row => row.client_key === stop.client_key)?.title ?? 'Plan card';
        return stop.pair_key ? `Rewrite of ${title}: the new card` : `New plan card: ${title}`;
    }
    case 'generated_material_addition': return `New material: ${(preview.generated_material_additions ?? []).find(row => row.client_key === stop.client_key)?.title ?? 'Material'}`;
    case 'exercise_addition': {
        const title = preview.exercise_additions?.find(row => row.client_key === stop.client_key)?.title ?? 'Exercise';
        return stop.pair_key ? `Rewrite of ${title}: the new exercise` : `New exercise: ${title}`;
    }
    case 'exercise_addition_step': {
        const exercise = preview.exercise_additions?.find(row => row.client_key === stop.parent_key);
        const activity = exercise?.steps.find(row => row.client_key === stop.client_key);
        // An activity of a rewritten exercise is named as its rewrite.
        const rewrite = preview.review_changes?.some(row => row.kind === 'exercise_addition' && row.client_key === stop.parent_key && row.pair_key);
        return `${rewrite ? `Rewrite of ${exercise?.title ?? 'Exercise'}: the new exercise` : `New exercise: ${exercise?.title ?? 'Exercise'}`} · ${activity?.title || (activityTypeLabel[activity?.class_name ?? ''] ?? 'Activity')}`;
    }
    case 'exercise_removal': {
        const title = preview.replaced_exercises?.find(row => row.id === stop.target_id)?.title ?? names.exercises?.find(row => row.id === stop.target_id)?.title ?? 'Exercise';
        return stop.pair_key ? `Rewrite of ${title}` : `Remove exercise: ${title}`;
    }
    case 'plan_section_removal': {
        const title = preview.replaced_plan_sections?.find(row => row.id === stop.target_id)?.title ?? section(stop.target_id);
        return stop.pair_key ? `Rewrite of ${title}` : `Remove plan card: ${title}`;
    }
    case 'quiz_field': {
        const change = preview.quiz_changes?.find(row => row.target_type === stop.target_type && row.target_id === stop.target_id && row.field === stop.field);
        const field = quizFieldLabels[(stop.field ?? 'title') as keyof typeof quizFieldLabels] ?? stop.field;
        if (stop.target_type === 'exercise') return `Exercise · ${field}`;
        const owner = step(stop.step_id);
        return stop.target_type === 'exercise_step' ? `${owner} · ${field}` : `${owner} · ${change?.label ?? 'Item'} · ${field}`;
    }
    case 'quiz_settings': return `${stop.target_type === 'exercise' ? 'Exercise' : step(stop.step_id)} · Other settings`;
    case 'choice_addition': return `${step(stop.step_id)} · New ${preview.quiz_choice_additions?.find(row => row.client_key === stop.client_key)?.meaning ? 'matching pair' : 'answer choice'}`;
    case 'choice_deletion': return `${step(stop.step_id)} · Remove ${preview.quiz_choice_deletions?.find(row => row.choice_id === stop.target_id)?.choice_text ?? 'answer choice'}`;
    case 'choice_order': return `${step(stop.step_id)} · Answer order`;
    case 'speaking_addition': return `${step(stop.step_id)} · New speaking target`;
    case 'speaking_deletion': return `${step(stop.step_id)} · Remove speaking target`;
    case 'speaking_order': return `${step(stop.step_id)} · Speaking target order`;
    case 'typed_addition': return `${step(stop.step_id)} · New accepted answer`;
    case 'typed_deletion': return `${step(stop.step_id)} · Remove accepted answer`;
    case 'typed_order': return `${step(stop.step_id)} · Accepted answer order`;
    case 'line_addition': return `${step(stop.step_id)} · New dialogue line`;
    case 'line_deletion': return `${step(stop.step_id)} · Remove dialogue line`;
    case 'line_order': return `${step(stop.step_id)} · Dialogue line order`;
    case 'step_addition': {
        const addition = preview.exercise_step_additions?.find(row => row.client_key === stop.client_key);
        return `New ${(activityTypeLabel[addition?.class_name ?? ''] ?? 'activity').toLowerCase()}: ${addition?.title ?? 'Activity'}`;
    }
    case 'step_deletion': return `Remove activity: ${preview.exercise_step_deletions?.find(row => row.step_id === stop.target_id)?.step_title ?? step(stop.target_id)}`;
    case 'step_order': return `${names.exercises?.find(row => row.id === stop.exercise_id)?.title ?? 'Exercise'} · Activity order`;
    }
}

/** One field's saved and proposed value, as text, for a compact comparison of the current change. */
export interface StopComparison { label: string; before: string; after: string }

function comparisonText(field: string, value: unknown): string {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) return '(empty)';
    if (Array.isArray(value)) return value.map(item => typeof item === 'string' ? item : JSON.stringify(item)).join(' ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    if (field === 'readiness_status' || field === 'status') return ({ ready: 'Ready', draft: 'Draft', review: 'Review' } as Record<string, string>)[String(value)] ?? String(value);
    return String(value);
}

/** The saved and proposed values a field-level stop changes; empty for structural stops such as new activities. */
export function stopComparison(stop: IVerbaScholaAiReviewChange, preview: IVerbaScholaAiPreview): StopComparison[] {
    const rows = (changes: { field: string; before?: unknown; after?: unknown }[], labels: Record<string, string>) =>
        changes.map(row => ({ label: labels[row.field] ?? row.field, before: comparisonText(row.field, row.before), after: comparisonText(row.field, row.after) }));
    switch (stop.kind) {
    case 'lesson_field': return rows((preview.lesson_changes ?? []).filter(row => row.field === stop.field), lessonFieldLabels);
    case 'plan_section_field': return rows((preview.plan_section_changes ?? []).filter(row => row.field === stop.field), rowFieldLabels);
    case 'generated_material_field': return rows((preview.generated_material_changes ?? []).filter(row => row.field === stop.field), rowFieldLabels);
    case 'language_target_field': return rows((preview.language_target_changes ?? []).filter(row => row.field === stop.field), rowFieldLabels);
    case 'content_replacement': return rows((preview.content_replacements ?? [])
        .filter(row => row.target_type === stop.target_type && row.target_id === stop.target_id).flatMap(row => row.changes), rowFieldLabels);
    case 'quiz_field': return rows((preview.quiz_changes ?? []).filter(row => row.target_type === stop.target_type && row.target_id === stop.target_id
        && row.field === stop.field), quizFieldLabels as Record<string, string>);
    default: return [];
    }
}

/** The plan panel that shows a stop, using the saved or proposed section type. */
export function stopPlanPanel(stop: IVerbaScholaAiReviewChange, preview: IVerbaScholaAiPreview, sections: IVerbaScholaLessonPlanSection[]): typeof PLAN_PANEL_ORDER[number] {
    if (stop.kind === 'generated_material_field' || stop.kind === 'generated_material_addition'
        || (stop.kind === 'content_replacement' && stop.target_type === 'generated_material')) return 'materials';
    if (stop.kind === 'language_target_field' || stop.kind === 'target_addition') return 'language';
    if (stop.kind === 'lesson_field') return 'context';
    const type = stop.kind === 'plan_section_addition'
        ? (preview.plan_section_additions ?? []).find(row => row.client_key === stop.client_key)?.section_type
        : sections.find(row => row.id === stop.target_id)?.section_type
            ?? preview.replaced_plan_sections?.find(row => row.id === stop.target_id)?.section_type;
    return planSectionPanel(type);
}

/** Stops in the order a teacher meets them on the page, keeping the server's order within a panel or exercise. */
export function sortStops(stops: IVerbaScholaAiReviewChange[], page: ReviewPage, preview: IVerbaScholaAiPreview,
    sections: IVerbaScholaLessonPlanSection[] = []): IVerbaScholaAiReviewChange[] {
    const rank = (stop: IVerbaScholaAiReviewChange): number => {
        if (page === 'description') {
            if (stop.kind === 'lesson_field') {
                const index = DESCRIPTION_ORDER.indexOf(stop.field ?? '');
                // The targets card sits between the class details and the advanced teaching approach.
                return stop.field === 'pedagogy_approach' ? 20 : index < 0 ? 15 : index;
            }
            return 10;
        }
        if (page === 'plan') return PLAN_PANEL_ORDER.indexOf(stopPlanPanel(stop, preview, sections));
        return 0;
    };
    return stops.map((stop, index) => ({ stop, index })).sort((a, b) => rank(a.stop) - rank(b.stop) || a.index - b.index).map(item => item.stop);
}

/** The preview narrowed to some stops, for showing one change with the same renderers as the full list. */
export function previewForStops(preview: IVerbaScholaAiPreview, stops: IVerbaScholaAiReviewChange[]): IVerbaScholaAiPreview {
    const has = (kind: IVerbaScholaAiReviewChange['kind'], match: (stop: IVerbaScholaAiReviewChange) => boolean) => stops.some(stop => stop.kind === kind && match(stop));
    const readinessWith = (kind: 'plan_section_field' | 'generated_material_field', field: string) =>
        field === (kind === 'plan_section_field' ? 'readiness_status' : 'status') ? stops.some(stop => stop.kind === kind) : has(kind, stop => stop.field === field);
    const stepOrderExercises = new Set(stops.filter(stop => stop.kind === 'step_order').map(stop => stop.exercise_id));
    return {
        ...preview,
        review_changes: stops,
        lesson_changes: (preview.lesson_changes ?? []).filter(row => has('lesson_field', stop => stop.field === row.field)),
        language_target_changes: (preview.language_target_changes ?? []).filter(row => has('language_target_field', stop => stop.field === row.field)),
        vocabulary_additions: (preview.vocabulary_additions ?? []).filter(row => has('target_addition', stop => stop.client_key === row.client_key)),
        grammar_additions: (preview.grammar_additions ?? []).filter(row => has('target_addition', stop => stop.client_key === row.client_key)),
        plan_section_changes: (preview.plan_section_changes ?? []).filter(row => readinessWith('plan_section_field', row.field)),
        generated_material_changes: (preview.generated_material_changes ?? []).filter(row => readinessWith('generated_material_field', row.field)),
        content_replacements: (preview.content_replacements ?? []).filter(row => has('content_replacement', stop => stop.target_type === row.target_type && stop.target_id === row.target_id)),
        plan_section_additions: (preview.plan_section_additions ?? []).filter(row => has('plan_section_addition', stop => stop.client_key === row.client_key)),
        generated_material_additions: (preview.generated_material_additions ?? []).filter(row => has('generated_material_addition', stop => stop.client_key === row.client_key)),
        // A new exercise shown for some of its activities lists only those.
        exercise_additions: (preview.exercise_additions ?? []).filter(row => has('exercise_addition', stop => stop.client_key === row.client_key)
            || has('exercise_addition_step', stop => stop.parent_key === row.client_key))
            .map(row => has('exercise_addition', stop => stop.client_key === row.client_key) ? row
                : { ...row, steps: row.steps.filter(step => has('exercise_addition_step', stop => stop.parent_key === row.client_key && stop.client_key === step.client_key)) }),
        quiz_changes: (preview.quiz_changes ?? []).filter(row => has('quiz_field', stop => stop.target_type === row.target_type && stop.target_id === row.target_id
            && (stop.field === row.field || `${stop.field}`.replace('_src_url', '_alt') === row.field))),
        quiz_choice_additions: (preview.quiz_choice_additions ?? []).filter(row => has('choice_addition', stop => stop.client_key === row.client_key)),
        quiz_choice_deletions: (preview.quiz_choice_deletions ?? []).filter(row => has('choice_deletion', stop => stop.target_id === row.choice_id)),
        quiz_choice_order: (preview.quiz_choice_order ?? []).filter(row => has('choice_order', stop => stop.step_id === row.step_id)),
        speaking_target_additions: (preview.speaking_target_additions ?? []).filter(row => has('speaking_addition', stop => stop.client_key === row.client_key)),
        speaking_target_deletions: (preview.speaking_target_deletions ?? []).filter(row => has('speaking_deletion', stop => stop.target_id === row.link_id)),
        speaking_target_order: (preview.speaking_target_order ?? []).filter(row => has('speaking_order', stop => stop.step_id === row.step_id)),
        typed_answer_additions: (preview.typed_answer_additions ?? []).filter(row => has('typed_addition', stop => stop.client_key === row.client_key)),
        typed_answer_deletions: (preview.typed_answer_deletions ?? []).filter(row => has('typed_deletion', stop => stop.target_id === row.link_id)),
        typed_answer_order: (preview.typed_answer_order ?? []).filter(row => has('typed_order', stop => stop.step_id === row.step_id)),
        conversation_line_additions: (preview.conversation_line_additions ?? []).filter(row => has('line_addition', stop => stop.client_key === row.client_key)),
        conversation_line_deletions: (preview.conversation_line_deletions ?? []).filter(row => has('line_deletion', stop => stop.target_id === row.link_id)),
        conversation_line_order: (preview.conversation_line_order ?? []).filter(row => has('line_order', stop => stop.parent_id === row.group_id)),
        exercise_step_additions: (preview.exercise_step_additions ?? []).filter(row => has('step_addition', stop => stop.client_key === row.client_key)),
        exercise_step_deletions: (preview.exercise_step_deletions ?? []).filter(row => has('step_deletion', stop => stop.target_id === row.step_id)),
        exercise_step_order: (preview.exercise_step_order ?? []).filter(row => stepOrderExercises.has(row.exercise_id ?? preview.generation.target_id) || (stepOrderExercises.size > 0 && row.exercise_id == null)),
        // A replacement's removals show the saved rows they delete (one stop: that row; the whole proposal: the set).
        replaced_plan_sections: (preview.replaced_plan_sections ?? []).filter(row => stops.some(stop => stop.group === 'replace'
            || (stop.kind === 'plan_section_removal' && stop.target_id === row.id))),
        replaced_exercises: (preview.replaced_exercises ?? []).filter(row => stops.some(stop => stop.group === 'replace'
            || (stop.kind === 'exercise_removal' && stop.target_id === row.id))),
        warnings: [],
        pinyin_review: null,
    };
}

export interface PlanReviewContent {
    lesson: IVerbaScholaLesson;
    sections: IVerbaScholaLessonPlanSection[];
    materials: IVerbaScholaLessonGeneratedMaterial[];
    vocabulary: IVerbaScholaLessonVocabularyTarget[];
    grammar: IVerbaScholaLessonGrammarTarget[];
}

/**
 * The lesson a saved version was made from, for showing it again: its details, plan cards, materials and targets as
 * the proposal saw them (rows made or removed since are left out or back), each row filled from today's where the
 * snapshot keeps fewer fields.
 */
export function replayBase(current: PlanReviewContent, before: IVerbaScholaAiPreview['before_content']): PlanReviewContent {
    if (!before) return current;
    const rows = <T extends { id: number }>(saved: Record<string, unknown>[] | undefined, today: T[]): T[] =>
        saved ? saved.map(row => ({ ...(today.find(item => item.id === row.id) ?? {}), ...row }) as unknown as T) : today;
    return {
        lesson: { ...current.lesson, ...(before.lesson ?? {}) } as IVerbaScholaLesson,
        sections: rows(before.plan_sections, current.sections),
        materials: rows(before.generated_materials, current.materials),
        vocabulary: rows(before.vocabulary, current.vocabulary),
        grammar: rows(before.grammar, current.grammar),
    };
}

/** Ids the browser gives proposed rows, below every saved id, keyed by kind and draft key. */
export type ReviewRowIds = Record<string, number>;

function nextOrder(rows: { order_index?: number | null }[]): number {
    return Math.max(0, ...rows.map(row => row.order_index ?? -1)) + 1;
}

/** The lesson description and plan as Apply would leave them: saved rows with every proposed change written. */
export function projectPlanContent(saved: PlanReviewContent, preview: IVerbaScholaAiPreview): { content: PlanReviewContent; rowIds: ReviewRowIds } {
    const rowIds: ReviewRowIds = {};
    let synthetic = 0;
    const newId = (kind: string, key: string) => { synthetic -= 1; rowIds[`${kind}:${key}`] = synthetic; return synthetic; };
    const target = preview.generation;
    const lesson = { ...saved.lesson, ...Object.fromEntries((preview.lesson_changes ?? []).map(change => [change.field, change.after])) } as IVerbaScholaLesson;
    const replacement = (kind: 'plan_section' | 'generated_material', id: number) =>
        Object.fromEntries((preview.content_replacements ?? []).filter(row => row.target_type === kind && row.target_id === id)
            .flatMap(row => row.changes.map(change => [change.field, change.after])));
    const replaced = new Set((preview.replaced_plan_sections ?? []).map(row => row.id));
    const sections = saved.sections.filter(row => !replaced.has(row.id)).map(row => ({
        ...row, ...replacement('plan_section', row.id),
        ...(target.target_type === 'plan_section' && target.target_id === row.id
            ? Object.fromEntries((preview.plan_section_changes ?? []).map(change => [change.field, change.after])) : {}),
    }) as IVerbaScholaLessonPlanSection);
    let order = nextOrder(sections);
    for (const row of preview.plan_section_additions) {
        sections.push({ id: newId('plan_section', row.client_key), verbaschola_lesson_id: saved.lesson.id, order_index: order++, title: row.title,
            body: row.body, duration_minutes: row.duration_minutes, section_type: row.section_type, readiness_status: 'draft', authoring_source: 'suggested' });
    }
    const materials = saved.materials.map(row => ({
        ...row, ...replacement('generated_material', row.id),
        ...(target.target_type === 'generated_material' && target.target_id === row.id
            ? Object.fromEntries((preview.generated_material_changes ?? []).map(change => [change.field, change.after])) : {}),
    }) as IVerbaScholaLessonGeneratedMaterial);
    order = nextOrder(materials);
    for (const row of preview.generated_material_additions) {
        materials.push({ id: newId('generated_material', row.client_key), verbaschola_lesson_id: saved.lesson.id, order_index: order++, title: row.title,
            body_text: row.body_text, material_format: row.material_format, status: 'draft', authoring_source: 'suggested', asset_s3_key: null });
    }
    const targetChanges = Object.fromEntries((preview.language_target_changes ?? []).map(change => [change.field, change.after]));
    const vocabulary = saved.vocabulary.map(row => target.target_type === 'vocabulary_target' && target.target_id === row.id ? { ...row, ...targetChanges } as IVerbaScholaLessonVocabularyTarget : row);
    order = nextOrder(vocabulary);
    for (const row of preview.vocabulary_additions) {
        vocabulary.push({ id: newId('vocabulary_target', row.client_key), verbaschola_lesson_id: saved.lesson.id, order_index: order++, hanzi: row.hanzi,
            pinyin: row.pinyin, meaning: row.meaning, source: row.source, is_review: false });
    }
    const grammar = saved.grammar.map(row => target.target_type === 'grammar_target' && target.target_id === row.id ? { ...row, ...targetChanges } as IVerbaScholaLessonGrammarTarget : row);
    order = nextOrder(grammar);
    for (const row of preview.grammar_additions) {
        grammar.push({ id: newId('grammar_target', row.client_key), verbaschola_lesson_id: saved.lesson.id, order_index: order++, pattern: row.pattern,
            meaning: row.meaning, example: row.example, source: row.source });
    }
    return { content: { lesson, sections, materials, vocabulary, grammar }, rowIds };
}

/** The row a stop changes, as an id in the version on screen (``shown``: whether it has this change), when it exists there. */
export function stopRowId(stop: IVerbaScholaAiReviewChange, shown: boolean, rowIds: ReviewRowIds): number | undefined {
    if (stop.client_key) {
        const kind = stop.kind === 'target_addition' ? stop.target_type : stop.kind === 'plan_section_addition' ? 'plan_section'
            : stop.kind === 'generated_material_addition' ? 'generated_material' : stop.kind === 'exercise_addition' ? 'exercise'
                : stop.kind === 'step_addition' || stop.kind === 'exercise_addition_step' ? 'exercise_step' : undefined;
        return shown && kind ? rowIds[`${kind}:${stop.client_key}`] : undefined;
    }
    return stop.target_id;
}

/** The rows a teacher can edit in Your version: lesson content, exercises, and an activity's question, answers and lines. */
export type ReviewEditKind = 'lesson' | 'plan_section' | 'generated_material' | 'vocabulary_target' | 'grammar_target' | 'exercise'
    | 'exercise_step' | 'exercise_step_choice' | 'exercise_step_sentence' | 'exercise_step_conversation';
/** The activity rows: an edit to one names the activity or exercise it sits in. */
export const ACTIVITY_EDIT_KINDS: ReadonlySet<ReviewEditKind> = new Set(['exercise_step', 'exercise_step_choice', 'exercise_step_sentence', 'exercise_step_conversation']);
/** The fields of each activity row Your version can change, as the review's save takes them (never type, order or parent). */
export const ACTIVITY_EDIT_FIELDS: Readonly<Record<string, readonly string[]>> = {
    exercise_step: ['title', 'prompt_text', 'prompt_pinyin', 'new_word_info_text', 'yes_button_text', 'no_button_text', 'maybe_button_text',
        'yes_response', 'no_response', 'maybe_response', 'spoken_yes_response', 'spoken_no_response', 'left_match', 'right_match', 'display_pinyin',
        'is_hidden', 'image_src_url', 'instructional_content_json', 'check_content_json', 'speaking_content_json', 'flow_content_json',
        'open_response_content_json'],
    exercise_step_choice: ['choice_text', 'choice_pinyin', 'is_correct', 'choice_content_json', 'sentence_text', 'sentence_pinyin'],
    exercise_step_sentence: ['speaker_name', 'student_response', 'student_response_pinyin', 'sentence_text', 'sentence_pinyin'],
    exercise_step_conversation: ['name'],
};
/** A saved row by id, or a proposed new row by its draft key. */
export interface ReviewRowRef { id?: number; clientKey?: string }
/** Where an activity row sits: a saved exercise and activity, or a proposed new activity (its draft key). */
export interface ReviewEditPlace { exerciseId?: number; stepId?: number; parentKey?: string }
/** The activity or script a row is added under, or whose list is put in order. */
export interface ReviewRowParent extends ReviewRowRef { kind: 'exercise_step' | 'exercise_step_conversation' }
/**
 * An answer, sentence or line the teacher adds (``create``, under ``parent`` and a ``teacher-`` draft key; its
 * fields are its values), removes (``delete``), or a parent's whole list put in ``order``.
 */
export interface ReviewRowStructure { action?: 'create' | 'delete' | 'order'; parent?: ReviewRowParent; order?: ReviewRowRef[] }
/** The start of the draft key of a row the teacher adds, which no AI change creates. */
export const TEACHER_KEY_PREFIX = 'teacher-';
/**
 * A teacher's edit to one row of Your version, kept in the review draft until the review is saved. ``before`` holds
 * the saved values of the edited fields when the review showed them, so a save never overwrites a newer change.
 * An activity row's edit also says where the row sits, so a change that removes or leaves out that activity finds it.
 */
export interface ReviewEdit extends ReviewRowRef, ReviewEditPlace, ReviewRowStructure { kind: ReviewEditKind; fields: Record<string, unknown>; before?: Record<string, unknown> }

/** One draft entry per row (an added row's later edits join its addition), per removal, and per parent's order. */
export function reviewEditKey(kind: ReviewEditKind, ref: ReviewRowRef & ReviewRowStructure): string {
    if (ref.action === 'delete') return `${kind}:delete:${ref.clientKey ?? ref.id}`;
    if (ref.action === 'order') return `${kind}:order:${ref.parent?.kind}:${ref.parent?.clientKey ?? ref.parent?.id}`;
    return ref.clientKey ? `${kind}:new:${ref.clientKey}` : `${kind}:${ref.id}`;
}

/**
 * A row a stop writes (``fields`` null: the whole row, as an addition or removal); ``within``: every activity row inside
 * the exercise or activity it removes or creates.
 */
export type StopTouch = ReviewRowRef & { kind: ReviewEditKind; fields: string[] | null; within?: ReviewEditPlace };

/** A linked sentence's text as an activity edit names it (the proposal's ``text`` and ``pinyin``). */
/**
 * Where a field an AI change to an activity reports lives among the columns the activity's editors save: a linked
 * sentence's text and pinyin, or a key inside one of the activity's JSON columns (its options, pictures and notes),
 * which the editors save whole.
 */
const ACTIVITY_FIELD_COLUMN: Record<string, Record<string, string>> = {
    exercise_step: { question_image_src_url: 'check_content_json', question_image_alt: 'check_content_json', english: 'instructional_content_json',
        image_alt: 'instructional_content_json', speaking_options: 'speaking_content_json', line_english: 'speaking_content_json',
        blank_texts: 'speaking_content_json', intro: 'flow_content_json', options: 'flow_content_json' },
    exercise_step_choice: { image_src_url: 'choice_content_json', image_alt: 'choice_content_json', left_image_src_url: 'choice_content_json',
        left_image_alt: 'choice_content_json', right_image_src_url: 'choice_content_json', right_image_alt: 'choice_content_json',
        matching_meaning: 'choice_content_json', choice_info: 'choice_content_json' },
    exercise_step_sentence: { text: 'sentence_text', pinyin: 'sentence_pinyin' },
};
const JSON_COLUMNS = new Set(['check_content_json', 'instructional_content_json', 'speaking_content_json', 'flow_content_json', 'choice_content_json']);
/** The column of an activity row an AI change's field is saved in. */
export function activityColumn(targetType: string | undefined, field: string): string {
    return ACTIVITY_FIELD_COLUMN[targetType ?? '']?.[field] ?? field;
}
/**
 * Fields whose AI change also gives the row a new linked sentence made from them: an answer read aloud, a typed
 * accepted answer. That sentence's text and pinyin are what the server makes of the answer, so the change writes them
 * too, with values the page does not know.
 */
const RELINKING_FIELDS: Record<string, readonly string[]> = {
    exercise_step_choice: ['choice_text', 'choice_pinyin'],
    exercise_step_sentence: ['student_response', 'student_response_pinyin'],
};
const LINKED_SENTENCE_FIELDS = ['sentence_text', 'sentence_pinyin'];
function relinksSentence(targetType: string | undefined, field: string | undefined): boolean {
    return Boolean(RELINKING_FIELDS[targetType ?? '']?.includes(field ?? ''));
}

/** Whether an edit is to a row a stop writes. */
export function touchesEdit(target: StopTouch, edit: ReviewEdit): boolean {
    if (target.within) {
        const place = target.within;
        return (place.exerciseId !== undefined && edit.exerciseId === place.exerciseId) || (place.stepId !== undefined && edit.stepId === place.stepId)
            || (place.parentKey !== undefined && edit.parentKey === place.parentKey);
    }
    return edit.kind === target.kind && (target.clientKey ? edit.clientKey === target.clientKey : !edit.clientKey && edit.id === target.id);
}

/** The rows a stop writes, to find edits it would replace or drop. */
export function stopTouches(stop: IVerbaScholaAiReviewChange, preview: IVerbaScholaAiPreview): StopTouch[] {
    const row = (kind: ReviewEditKind, fields: string[] | null): StopTouch[] => [{ kind, id: stop.target_id, fields }];
    const created = (kind: ReviewEditKind): StopTouch[] => [{ kind, clientKey: stop.client_key, fields: null }];
    switch (stop.kind) {
    case 'lesson_field': return row('lesson', [stop.field ?? '']);
    case 'plan_section_field': case 'generated_material_field': case 'language_target_field':
        return row(stop.target_type as ReviewEditKind, [stop.field ?? '']);
    case 'content_replacement': return row(stop.target_type as ReviewEditKind, (preview.content_replacements ?? [])
        .filter(item => item.target_type === stop.target_type && item.target_id === stop.target_id).flatMap(item => item.changes.map(change => change.field)));
    case 'quiz_field': {
        const field = stop.field ?? '';
        if (!stop.target_type) return [];
        // A new answer text also replaces the row's linked sentence, so the teacher's sentence edits are written over too.
        const sentence = relinksSentence(stop.target_type, field) ? LINKED_SENTENCE_FIELDS : [];
        return row(stop.target_type as ReviewEditKind, [activityColumn(stop.target_type, field), ...sentence]);
    }
    // An activity's display settings are no field its editor changes.
    case 'quiz_settings': return stop.target_type === 'exercise' ? row('exercise', null) : [];
    case 'plan_section_removal': return row('plan_section', null);
    case 'exercise_removal': return [...row('exercise', null), { kind: 'exercise', fields: null, within: { exerciseId: stop.target_id } }];
    case 'step_deletion': return [...row('exercise_step', null), { kind: 'exercise_step', fields: null, within: { stepId: stop.target_id } }];
    case 'choice_deletion': return row('exercise_step_choice', null);
    case 'speaking_deletion': case 'typed_deletion': case 'line_deletion': return row('exercise_step_sentence', null);
    case 'step_addition': case 'exercise_addition_step':
        return [...created('exercise_step'), { kind: 'exercise_step', fields: null, within: { parentKey: stop.client_key } }];
    case 'choice_addition': return created('exercise_step_choice');
    case 'speaking_addition': case 'typed_addition': case 'line_addition': return created('exercise_step_sentence');
    case 'target_addition': return [{ kind: stop.target_type as ReviewEditKind, clientKey: stop.client_key, fields: null }];
    case 'plan_section_addition': return [{ kind: 'plan_section', clientKey: stop.client_key, fields: null }];
    case 'generated_material_addition': return [{ kind: 'generated_material', clientKey: stop.client_key, fields: null }];
    case 'exercise_addition': return [{ kind: 'exercise', clientKey: stop.client_key, fields: null }];
    default: return [];
    }
}

/** Edits a stop's change would replace (the same fields) or drop (a whole row it adds or removes). */
export function editsTouchedBy(stops: IVerbaScholaAiReviewChange[], preview: IVerbaScholaAiPreview, edits: ReviewEdit[]): { edit: ReviewEdit; fields: string[] | null }[] {
    const touched: { edit: ReviewEdit; fields: string[] | null }[] = [];
    for (const stop of stops) {
        for (const target of stopTouches(stop, preview)) {
            for (const edit of edits.filter(item => touchesEdit(target, item))) {
                const fields = target.fields === null ? null : target.fields.filter(field => field in edit.fields);
                if ((fields === null || fields.length) && !touched.some(item => item.edit === edit && item.fields === null)) touched.push({ edit, fields });
            }
        }
    }
    return touched;
}

/** Rows with the teacher's edits on top; ``clientKeyOf`` names a proposed new row shown under a temporary id. */
export function withEdits<T extends { id: number }>(rows: T[], kind: ReviewEditKind, edits: ReviewEdit[], clientKeyOf: (id: number) => string | undefined): T[] {
    const own = edits.filter(edit => edit.kind === kind);
    if (!own.length) return rows;
    return rows.map(row => {
        const clientKey = clientKeyOf(row.id);
        const edit = own.find(item => clientKey ? item.clientKey === clientKey : !item.clientKey && item.id === row.id);
        return edit ? { ...row, ...edit.fields } as T : row;
    });
}

/** The new rows the chosen stops create (``kind:client_key``), to tell which edits to proposed new rows a save can write. */
export function createdRows(stops: IVerbaScholaAiReviewChange[], preview: IVerbaScholaAiPreview): Set<string> {
    return new Set(stops.flatMap(stop => stopTouches(stop, preview)).flatMap(target => target.clientKey && !target.within ? [`${target.kind}:${target.clientKey}`] : []));
}

/** Whether a save creating ``created`` can write an edit: to a saved row, or to a new row it creates (or one inside a new activity it creates). */
export function editWritable(edit: ReviewEdit, created: Set<string>): boolean {
    // A row the teacher adds needs its parent, and an order its list's parent, on the lesson after the save.
    if (edit.action === 'create' || edit.action === 'order') return !edit.parent?.clientKey || created.has(`${edit.parent.kind}:${edit.parent.clientKey}`)
        || (edit.parentKey !== undefined && created.has(`exercise_step:${edit.parentKey}`));
    if (!edit.clientKey) return true;
    return created.has(`${edit.kind}:${edit.clientKey}`) || (edit.parentKey !== undefined && created.has(`exercise_step:${edit.parentKey}`));
}

/** Why the teacher's edit cannot be part of a save: it is to an item that save does not create, or one it removes. */
export type UnsaveableReason = 'ai_item' | 'removed_by_ai';
/** An activity's maps keyed by line id inside its ``speaking_content_json``: blanked displays and line translations. */
const LINE_MAP_FIELDS = ['blank_text_by_line_id', 'line_english_by_id'];
/** The AI changes that remove a whole activity or exercise, with everything in it. */
const WHOLE_REMOVALS: Record<string, keyof ReviewEditPlace> = { step_deletion: 'stepId', exercise_removal: 'exerciseId' };
/** The AI changes that remove a row; leaving one out keeps the row, and every edit inside it stays writable. */
export const REMOVAL_STOP_KINDS: ReadonlySet<string> = new Set(['plan_section_removal', 'exercise_removal', 'step_deletion', 'choice_deletion',
    'speaking_deletion', 'typed_deletion', 'line_deletion']);

/**
 * The edits one save of Your version writes, given the AI changes it keeps (``chosen``; none for Save my edits only),
 * and those it cannot write. The projection of Your version, the save and Save my edits only all take their edits from
 * here, so what the page shows is what is saved.
 * - An edit to a saved row, and a row the teacher adds under a saved activity, are written; one inside an activity or
 *   exercise a kept AI change removes is not (``removed_by_ai``).
 * - An edit to a new item, or a row added under one, is written only with the AI change that creates the item
 *   (``ai_item`` otherwise).
 * - A removal of a new item the save does not create has nothing to remove and is left out unlisted.
 * - An order names only rows the save leaves in its list: a row the save removes or does not create is taken out of it
 *   (the server keeps any other row after its neighbour), and an order left naming fewer than two rows is moot.
 */
export function planReviewSave(edits: ReviewEdit[], chosen: IVerbaScholaAiReviewChange[], preview: IVerbaScholaAiPreview)
    : { send: ReviewEdit[]; unsaveable: { edit: ReviewEdit; reason: UnsaveableReason }[] } {
    const created = createdRows(chosen, preview);
    const removed = chosen.filter(stop => WHOLE_REMOVALS[stop.kind] && stop.target_id != null)
        .map(stop => ({ place: WHOLE_REMOVALS[stop.kind], id: stop.target_id as number }));
    const underRemoved = (edit: ReviewEdit) => removed.some(item => edit[item.place] === item.id);
    const insideCreated = (edit: ReviewEdit) => edit.parentKey !== undefined && created.has(`exercise_step:${edit.parentKey}`);
    const unsaveable: { edit: ReviewEdit; reason: UnsaveableReason }[] = [];
    const keep = new Set<ReviewEdit>();
    const addedRows = new Set<string>();
    const removedRows = new Set<string>();
    for (const edit of edits) {
        if (edit.action === 'order') continue;
        if (edit.action === 'delete') {
            // The AI removes the whole activity anyway; a new item the save does not create is not on the lesson.
            if (underRemoved(edit) || (edit.clientKey && !created.has(`${edit.kind}:${edit.clientKey}`) && !insideCreated(edit))) continue;
            keep.add(edit); removedRows.add(`${edit.kind}:${edit.clientKey ?? edit.id}`);
            continue;
        }
        if (underRemoved(edit)) { unsaveable.push({ edit, reason: 'removed_by_ai' }); continue; }
        if (!editWritable(edit, created)) { unsaveable.push({ edit, reason: 'ai_item' }); continue; }
        keep.add(edit);
        if (edit.action === 'create' && edit.clientKey) addedRows.add(`${edit.kind}:${edit.clientKey}`);
    }
    // An activity's blanks and line translations name only lines on the lesson after this save: a line the teacher added
    // only when its addition is sent, a new line of the proposal only when this save creates it (or its new activity).
    const lineKept = (edit: ReviewEdit, key: string) => /^\d+$/.test(key) || (key.startsWith(TEACHER_KEY_PREFIX)
        ? addedRows.has(`exercise_step_sentence:${key}`)
        : created.has(`exercise_step_sentence:${key}`) || insideCreated(edit) || (edit.clientKey !== undefined && created.has(`exercise_step:${edit.clientKey}`)));
    const trimLines = (edit: ReviewEdit, values: Record<string, unknown> | undefined) => {
        const options = values?.speaking_content_json;
        if (!options || typeof options !== 'object' || Array.isArray(options)) return values;
        const speaking = { ...(options as Record<string, unknown>) };
        let trimmed = false;
        for (const map of LINE_MAP_FIELDS) {
            const byId = speaking[map];
            if (!byId || typeof byId !== 'object' || Array.isArray(byId)) continue;
            const kept = Object.fromEntries(Object.entries(byId as Record<string, unknown>).filter(([key]) => lineKept(edit, key)));
            if (Object.keys(kept).length !== Object.keys(byId).length) { speaking[map] = kept; trimmed = true; }
        }
        return trimmed ? { ...values, speaking_content_json: speaking } : values;
    };
    const withLines = (edit: ReviewEdit): ReviewEdit => {
        if (edit.kind !== 'exercise_step' || edit.action) return edit;
        const fields = trimLines(edit, edit.fields)!;
        const before = trimLines(edit, edit.before);
        return fields === edit.fields && before === edit.before ? edit : { ...edit, fields, ...(before ? { before } : {}) };
    };
    const send: ReviewEdit[] = [];
    for (const edit of edits) {
        if (edit.action !== 'order') { if (keep.has(edit)) send.push(withLines(edit)); continue; }
        if (underRemoved(edit)) { unsaveable.push({ edit, reason: 'removed_by_ai' }); continue; }
        if (!editWritable(edit, created)) { unsaveable.push({ edit, reason: 'ai_item' }); continue; }
        // A row the teacher added is in the list only when this save sends its addition, also inside a new activity.
        const order = (edit.order ?? []).filter(ref => !removedRows.has(`${edit.kind}:${ref.clientKey ?? ref.id}`) && (!ref.clientKey
            || addedRows.has(`${edit.kind}:${ref.clientKey}`) || (!ref.clientKey.startsWith(TEACHER_KEY_PREFIX)
                && (created.has(`${edit.kind}:${ref.clientKey}`) || insideCreated(edit)))));
        if (order.length >= 2) send.push(order.length === (edit.order ?? []).length ? edit : { ...edit, order });
    }
    return { send, unsaveable };
}

/** The draft key of a proposed new row from the browser's temporary ids (``kind:client_key`` → id). */
export function clientKeyFromRowIds(rowIds: ReviewRowIds, kind: string): (id: number) => string | undefined {
    const byId = new Map(Object.entries(rowIds).filter(([key]) => key.startsWith(`${kind}:`)).map(([key, id]) => [id, key.slice(kind.length + 1)]));
    return id => byId.get(id);
}

/** The lesson description and plan of Your version with the teacher's edits on top. */
export function planContentWithEdits(content: PlanReviewContent, edits: ReviewEdit[], rowIds: ReviewRowIds): PlanReviewContent {
    if (!edits.length) return content;
    const lessonEdit = edits.find(edit => edit.kind === 'lesson');
    return {
        lesson: lessonEdit ? { ...content.lesson, ...lessonEdit.fields } as IVerbaScholaLesson : content.lesson,
        sections: withEdits(content.sections, 'plan_section', edits, clientKeyFromRowIds(rowIds, 'plan_section')),
        materials: withEdits(content.materials, 'generated_material', edits, clientKeyFromRowIds(rowIds, 'generated_material')),
        vocabulary: withEdits(content.vocabulary, 'vocabulary_target', edits, clientKeyFromRowIds(rowIds, 'vocabulary_target')),
        grammar: withEdits(content.grammar, 'grammar_target', edits, clientKeyFromRowIds(rowIds, 'grammar_target')),
    };
}

/** The fields of an editor's save whose values differ from the row on screen: what the teacher actually changed. */
export function changedFields(row: object | undefined, payload: object): Record<string, unknown> {
    const current = (row ?? {}) as Record<string, unknown>;
    return Object.fromEntries(Object.entries(payload).filter(([field, value]) => JSON.stringify(current[field] ?? null) !== JSON.stringify(value ?? null)));
}

/** The saved values of some fields of a saved row, as ``ReviewEdit.before``. */
export function savedValues(row: object | undefined, fields: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!row) return undefined;
    const saved = row as Record<string, unknown>;
    return Object.fromEntries(Object.keys(fields).map(field => [field, saved[field] ?? null]));
}

/** The value a stop's change writes to one field of a saved row, as the preview proposes it (``found`` false: not this stop's field). */
export function stopFieldValue(stop: IVerbaScholaAiReviewChange, preview: IVerbaScholaAiPreview, field: string): { found: boolean; value?: unknown } {
    const pick = (changes: { field: string; after?: unknown }[]) => {
        const change = changes.find(row => row.field === field);
        return change ? { found: true, value: change.after } : { found: false };
    };
    switch (stop.kind) {
    case 'lesson_field': return pick(preview.lesson_changes ?? []);
    case 'plan_section_field': return pick(preview.plan_section_changes ?? []);
    case 'generated_material_field': return pick(preview.generated_material_changes ?? []);
    case 'language_target_field': return pick(preview.language_target_changes ?? []);
    case 'content_replacement': return pick((preview.content_replacements ?? [])
        .filter(row => row.target_type === stop.target_type && row.target_id === stop.target_id).flatMap(row => row.changes));
    case 'quiz_field': {
        const changes = (preview.quiz_changes ?? []).filter(row => row.target_type === stop.target_type && row.target_id === stop.target_id);
        // A JSON column the AI writes in part holds what neither side alone knows: no value is expected of it. The
        // proposal's own staleness check still refuses the save if the row changed since the proposal was made.
        if (JSON_COLUMNS.has(field)) return { found: false };
        // Nor of the new linked sentence an answer's text change saves: the server makes it from the answer.
        if (relinksSentence(stop.target_type, stop.field) && LINKED_SENTENCE_FIELDS.includes(field)) return { found: false };
        const change = changes.find(row => activityColumn(stop.target_type, row.field) === field);
        return change ? { found: true, value: change.after } : { found: false };
    }
    default: return { found: false };
    }
}

/**
 * The values the lesson should hold in an edit's fields just before the edit is written: the saved values the review
 * showed, or, for fields the chosen AI changes write, the AI's values; for a row the save creates, the values the AI
 * proposed for it. Kept with an unsaved edit, so a later retry still refuses to overwrite someone else's change.
 */
export function expectedBeforeEdit(edit: ReviewEdit, chosen: IVerbaScholaAiReviewChange[], preview: IVerbaScholaAiPreview): Record<string, unknown> | undefined {
    // A removed saved row is checked on every field it showed (``before``), as an edit is on its own: it is removed only
    // while nobody has changed it since. Added and reordered rows check the list itself when saved; a removed new row,
    // created by the same save, has nothing to check.
    const removal = edit.action === 'delete' && !edit.clientKey;
    // A saved list's new order is written only while the list still holds the order it had when the teacher moved a
    // row in it (``before.order``, the saved order behind Your version then).
    if (edit.action === 'order') return !edit.parent?.clientKey && Array.isArray(edit.before?.order) ? { order: edit.before!.order } : undefined;
    if (edit.action && !removal) return undefined;
    // A row of a new activity holds what Your version showed for it (the AI's values, as the projection wrote them)
    // until someone changes it: also when its edit is written later, after another save created that row.
    if (edit.clientKey && ACTIVITY_EDIT_KINDS.has(edit.kind)) {
        // Its dialogue's line maps are keyed by the ids of lines the same save creates, which no projection can name.
        const shown = Object.fromEntries(Object.entries(edit.before ?? {}).filter(([field]) => field in edit.fields && field !== 'speaking_content_json'));
        return Object.keys(shown).length ? shown : undefined;
    }
    if (edit.clientKey) {
        // A row this save creates holds what the AI proposed for it, until someone changes it.
        const rows: { client_key: string }[] = edit.kind === 'plan_section' ? preview.plan_section_additions ?? []
            : edit.kind === 'generated_material' ? preview.generated_material_additions ?? [] : edit.kind === 'vocabulary_target' ? preview.vocabulary_additions ?? []
                : edit.kind === 'grammar_target' ? preview.grammar_additions ?? [] : edit.kind === 'exercise' ? preview.exercise_additions ?? [] : [];
        // A field the AI left out (an exercise's instructions or subtitle, say) is created empty.
        const proposed = (rows.find(row => row.client_key === edit.clientKey) ?? {}) as Record<string, unknown>;
        return Object.fromEntries(Object.keys(edit.fields).map(field => [field, field in proposed ? proposed[field] : null]));
    }
    // Only fields the teacher still edits are checked; a field handed back to the AI no longer is.
    const checked = (field: string) => removal || field in edit.fields;
    const expected: Record<string, unknown> = Object.fromEntries(Object.entries(edit.before ?? {}).filter(([field]) => checked(field)));
    for (const stop of chosen) {
        for (const target of stopTouches(stop, preview)) {
            if (target.kind !== edit.kind || target.clientKey || target.id !== edit.id || !target.fields) continue;
            for (const field of target.fields.filter(checked)) {
                const written = stopFieldValue(stop, preview, field);
                if (written.found) expected[field] = written.value; else delete expected[field];
            }
        }
    }
    // A new matching view links the activity's answers to new sentences or none, so their sentences' text after the AI
    // changes is not known here either.
    if (edit.kind === 'exercise_step_choice' && edit.stepId !== undefined && chosen.some(stop => stop.kind === 'quiz_field'
        && stop.target_type === 'exercise_step' && stop.target_id === edit.stepId && stop.field === 'left_match')) {
        for (const field of LINKED_SENTENCE_FIELDS) delete expected[field];
    }
    // A removal always says what it showed; with every field rewritten by the AI in ways the page cannot know, the saved
    // values stand (the save is then refused as changed rather than removing an answer unseen).
    if (removal && !Object.keys(expected).length) return edit.before && Object.keys(edit.before).length ? { ...edit.before } : undefined;
    return Object.keys(expected).length ? expected : undefined;
}
